import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Box, Button, Chip, TextField, InputAdornment, Typography, Alert } from '@mui/material';
import { Add, Edit, Delete, Search, SupervisorAccount } from '@mui/icons-material';
import { DataGridPremium, GridColDef, GridActionsCellItem } from '@mui/x-data-grid-premium';
import { ViewToolbar } from '@/components/ViewToolbar';
import { GRID_ROW_HEIGHT } from '@/lib/gridStyles';
import { useUsers, useDeleteUser } from '@/hooks/identity/useIdentity';
import { IdentityUser } from '@/types/identity/Identity';
import { useHasPermission } from '@/components/PermissionGate';
import { useAuth } from '@/contexts/AuthContext';
import { authService } from '@/services/auth/authService';
import { UserAccountTypeChip } from '@/core/UserAccountTypeChip';
import { resolveUserAccountType } from '@/core/userAccountType';

export function UserListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { refreshAuthState } = useAuth();
  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [actionError, setActionError] = useState<string | null>(null);

  const { data, isLoading, refetch } = useUsers(filter || undefined, page, pageSize);
  const deleteMutation = useDeleteUser();
  // Hidden entirely while impersonating — nested impersonation is forbidden (R3)
  const canImpersonate = useHasPermission('Nexo.Users.Impersonate') && !authService.isImpersonating();
  const currentUserId = authService.getCurrentUser()?.sub;

  const handleImpersonate = useCallback(async (userId: string) => {
    setActionError(null);
    try {
      await authService.impersonate(userId);
      // In-app transition — NOT a reload (D13b: the impersonation token is memory-only,
      // and a reload would drop it and restore the admin). Reset the React Query cache
      // (it holds data fetched as the admin) and re-read identity into the auth context.
      queryClient.clear();
      refreshAuthState();
      navigate('/');
    } catch (error) {
      setActionError(error instanceof Error ? error.message : t('identity.impersonateFailed'));
    }
  }, [queryClient, refreshAuthState, navigate, t]);

  const handleDelete = useCallback(
    (id: string, userName: string) => {
      if (!confirm(t('identity.confirmDeleteUser', { name: userName }))) return;
      setActionError(null);
      deleteMutation.mutate(id, {
        onSuccess: () => {
          refetch();
        },
        onError: () => setActionError(t('identity.deleteUserFailed')),
      });
    },
    [deleteMutation, refetch, t],
  );

  const columns = useMemo<GridColDef<IdentityUser>[]>(
    () => [
      {
        field: '_rowActions',
        type: 'actions',
        headerName: '',
        width: 120,
        getActions: (params) => {
          const actions = [
            <GridActionsCellItem
              key="edit"
              icon={<Edit />}
              label={t('common.edit')}
              onClick={() => navigate(`/core/users/${params.id}`)}
            />,
            <GridActionsCellItem
              key="delete"
              icon={<Delete />}
              label={t('common.delete')}
              onClick={() => handleDelete(params.id as string, params.row.userName)}
            />,
          ];
          // "Log in as" — not shown for the caller's own row or inactive users.
          // Admin-role targets are rejected server-side (the list DTO has no roles).
          if (canImpersonate && params.id !== currentUserId && params.row.isActive) {
            actions.push(
              <GridActionsCellItem
                key="impersonate"
                icon={<SupervisorAccount />}
                label={t('identity.impersonate')}
                title={t('identity.impersonateTitle')}
                onClick={() => handleImpersonate(params.id as string)}
              />,
            );
          }
          return actions;
        },
      },
      { field: 'userName', headerName: t('identity.username'), width: 180 },
      { field: 'email', headerName: t('identity.email'), width: 240 },
      { field: 'name', headerName: t('identity.firstName'), width: 140 },
      { field: 'surname', headerName: t('identity.lastName'), width: 140 },
      {
        // Derived, not stored: `valueGetter` resolves it so the column sorts,
        // filters and exports on the type rather than on the raw markers.
        field: 'accountType',
        headerName: t('identity.accountTypeColumn'),
        width: 120,
        sortable: true,
        valueGetter: (_value, row: IdentityUser) => resolveUserAccountType(row.extraProperties),
        // Guarded because the grid also renders rows the valueGetter never sees
        // (grouping/aggregation rows), where the cell has no type to show.
        renderCell: (params) =>
          params.value ? <UserAccountTypeChip accountType={params.value} /> : null,
      },
      {
        field: 'isActive',
        headerName: t('common.status'),
        width: 100,
        renderCell: (params) =>
          params.value ? (
            <Chip label={t('common.active')} size="small" color="success" />
          ) : (
            <Chip label={t('common.inactive')} size="small" color="default" />
          ),
      },
      {
        field: 'lockoutEnd',
        headerName: t('identity.locked'),
        width: 100,
        renderCell: (params) => {
          const lockedOut = params.value && new Date(params.value) > new Date();
          return lockedOut ? (
            <Chip label={t('identity.locked')} size="small" color="error" />
          ) : null;
        },
      },
      {
        field: 'creationTime',
        headerName: t('grid.created'),
        width: 160,
        type: 'dateTime',
        valueGetter: (value: string) => value ? new Date(value) : null,
      },
    ],
    [navigate, handleDelete, handleImpersonate, canImpersonate, currentUserId, t],
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <ViewToolbar onRefresh={() => refetch()}>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<Add />}
          onClick={() => navigate('/core/users/new')}
          sx={{ textTransform: 'none', fontWeight: 500, px: 2 }}
        >
          {t('identity.newUser')}
        </Button>
      </ViewToolbar>

      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', px: 2, pb: 2 }}>
        <Typography variant="h5" sx={{ mb: 1, fontWeight: 500 }}>
          {t('identity.users')}
        </Typography>
        {actionError && (
          <Alert severity="error" onClose={() => setActionError(null)} sx={{ mb: 1 }}>
            {actionError}
          </Alert>
        )}

      <Box sx={{ pb: 1 }}>
        <TextField
          placeholder={t('identity.searchUsers')}
          size="small"
          value={filter}
          onChange={(e) => {
            setFilter(e.target.value);
            setPage(0);
          }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
          sx={{ width: 300 }}
        />
      </Box>

      <Box sx={{ flex: 1, minHeight: 0 }}>
        <DataGridPremium
          rows={data?.items ?? []}
          columns={columns}
          rowCount={data?.totalCount ?? 0}
          loading={isLoading}
          paginationMode="server"
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={(model) => {
            setPage(model.page);
            setPageSize(model.pageSize);
          }}
          pageSizeOptions={[10, 25, 50, 100]}
          density="compact"
          rowHeight={GRID_ROW_HEIGHT}
          disableRowSelectionOnClick
          getRowId={(row) => row.id}
          onRowDoubleClick={(params) => navigate(`/core/users/${params.id}`)}
        />
      </Box>
      </Box>
    </Box>
  );
}
